import { Box, Card, CardContent, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMemo } from "react";
import type { Information } from "@/shared/lib/api/api-client";
import { formatDate } from "@/shared/utils";

const CardContentNoPadding = styled(CardContent)(`
	padding: 0;
  &:last-child {
    padding-bottom: 0;
  }
`);

interface InformationViewProps {
	jsonContents: Information[];
}

const CLOUDFRONT_DOMAIN = import.meta.env.VITE_CLOUDFRONT_DOMAIN;

const replaceFilenamesWithCloudFrontUrls = (body: string, informationId: number): string => {
	// {{filename:xxx}} プレースホルダーをCloudFront URLに置換
	let result = body.replace(/\{\{filename:([^}]+)\}\}/g, (_, filename) => {
		return `https://${CLOUDFRONT_DOMAIN}/images/home/detail/${informationId}/${filename}`;
	});

	result = result.replace(/(['"])\/images\/home\/detail\/([^'"]+)\1/g, (_, quote, path) => {
		return `${quote}https://${CLOUDFRONT_DOMAIN}/images/home/detail/${path}${quote}`;
	});

	result = result.replace(/src=\/images\/home\/detail\/([^\s>]+)/g, (_, path) => {
		return `src=https://${CLOUDFRONT_DOMAIN}/images/home/detail/${path}`;
	});

	return result;
};

export default function InformationView({ jsonContents }: InformationViewProps) {
	// biome-ignore lint/correctness/useExhaustiveDependencies: jsonContentsの参照が変わるたびに再計算されるのを防ぐため、IDの文字列のみを依存に含める
	const processedContents = useMemo(() => {
		return jsonContents.map((info) => ({
			...info,
			body: replaceFilenamesWithCloudFrontUrls(info.body, info.id),
		}));
	}, [jsonContents.map((info) => info.id).join(",")]);

	return (
		<Box
			sx={{
				backgroundColor: "#ff5f49",
				padding: "18px",
				borderRadius: "8px",
				width: "100%",
				maxWidth: "560px",
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
				gap: 2,
			}}
		>
			{processedContents.length > 0 ? (
				processedContents.map((json) => (
					<Card
						key={json.id}
						variant="outlined"
						sx={{
							width: "100%",
							maxWidth: "500px",
							display: "flex",
							flexDirection: "column",
							justifyContent: "flex-start",
							boxShadow: 2,
							borderRadius: 3,
							backgroundColor: "#FFFFFF",
							color: "#000000",
							overflow: "hidden",
						}}
					>
						<CardContentNoPadding>
							<Box
								sx={{
									px: 3,
									pt: 2.5,
									pb: 3,
								}}
							>
								{/* 日付 */}
								<Typography
									sx={{
										color: "#8c8c8c",
										fontSize: "12px",
										mb: 0.5,
										pl: 1,
									}}
								>
									{formatDate(new Date(json.date))}
								</Typography>

								{/* タイトル */}
								<Typography
									sx={{
										fontSize: "18px",
										fontWeight: "bold",
										mb: 2,
										pl: 1,
										color: "#ff5f49",
									}}
								>
									{json.title}
								</Typography>

								{/* ボーダー */}
								<Box
									sx={{
										height: "1px",
										backgroundColor: "#e0e0e0",
										mb: 3,
									}}
								/>

								{/* 本文 */}
								<Box
									sx={{
										pl: 1,
										"& p": {
											margin: 0,
											marginBottom: "1em",
											fontWeight: "medium",
											wordBreak: "break-word",
											overflowWrap: "break-word",
										},
										"& a": {
											textDecoration: "underline",
											color: "#000000",
										},
										"& img": {
											width: "80%",
											display: "block",
											margin: "auto",
										},
										"& ul": {
											paddingLeft: "1em",
											"& li": {
												paddingLeft: "1em",
												listStyle: "none",
												position: "relative",
												"&::before": {
													content: '"・"',
													marginLeft: "-1em",
													position: "absolute",
													left: 0,
												},
											},
										},
									}}
								>
									<Typography
										sx={{
											fontSize: "14px",
											lineHeight: 1.5,
											whiteSpace: "pre-wrap",
										}}
										dangerouslySetInnerHTML={{
											__html: json.body.replace(/\s*\n\s*/g, ""),
										}}
									/>
								</Box>
							</Box>
						</CardContentNoPadding>
					</Card>
				))
			) : (
				<Typography variant="body1" color="textSecondary">
					お知らせがありません
				</Typography>
			)}
		</Box>
	);
}
